import { useState, useCallback, useMemo } from 'react';

interface LoadingState<T> {
  isLoading: boolean;
  error: string | null;
  data: T | null;
}

export function useLoadingState<T = any>(initialData: T | null = null) {
  const [state, setState] = useState<LoadingState<T>>({
    isLoading: false,
    error: null,
    data: initialData
  });

  const setLoading = useCallback((loading: boolean) => {
    setState(prev => ({
      ...prev,
      isLoading: loading,
      error: loading ? null : prev.error
    }));
  }, []);

  const setError = useCallback((error: string | null) => {
    setState({
      isLoading: false,
      error,
      data: null
    });
  }, []);

  const setData = useCallback((data: T | null) => {
    setState({
      isLoading: false,
      error: null,
      data
    });
  }, []);

  const reset = useCallback(() => {
    setState({
      isLoading: false,
      error: null,
      data: initialData
    });
  }, [initialData]);

  const execute = useCallback(async (asyncOperation: () => Promise<T>) => {
    setLoading(true);
    try {
      const result = await asyncOperation();
      setData(result);
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An error occurred';
      setError(errorMessage);
      throw error;
    }
  }, [setLoading, setData, setError]);

  const isIdle = useMemo(() => 
    !state.isLoading && !state.error && state.data === null,
    [state.isLoading, state.error, state.data]
  );

  const isSuccess = useMemo(() => 
    !state.isLoading && !state.error && state.data !== null,
    [state.isLoading, state.error, state.data]
  );

  const isError = useMemo(() => 
    !state.isLoading && state.error !== null,
    [state.isLoading, state.error]
  );

  return {
    ...state,
    setLoading,
    setError,
    setData,
    reset,
    execute,
    isIdle,
    isSuccess,
    isError
  };
}